import { TimeRangePickerProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);
dayjs.extend(isBetween);

// Determine the start of the current financial year (July 1st of the relevant year)
const currentDate = dayjs();
const currentMonth = dayjs().month();
const currentYear = dayjs().year();
const startOfFinancialYear = dayjs()
  .set("year", currentMonth < 6 ? currentYear - 1 : currentYear)
  .month(6)
  .date(1);

// Calculate the start of the current quarter
const currentQuarterStart = dayjs().startOf("quarter");

export const calculateDateRangePresets = (
  minDate: Dayjs,
  maxDate: Dayjs
): TimeRangePickerProps["presets"] => {
  const allTime: [Dayjs, Dayjs] = [minDate, maxDate];
  const last3Months: [Dayjs, Dayjs] = [
    maxDate.subtract(3, "month").startOf("day"),
    maxDate.endOf("day"),
  ];
  const last6Months: [Dayjs, Dayjs] = [
    maxDate.subtract(6, "month").startOf("day"),
    maxDate.endOf("day"),
  ];
  const last9Months: [Dayjs, Dayjs] = [
    maxDate.subtract(9, "month").startOf("day"),
    maxDate.endOf("day"),
  ];
  const last12Months: [Dayjs, Dayjs] = [
    maxDate.subtract(12, "month").startOf("day"),
    maxDate.endOf("day"),
  ];

  const adjustEndDate = (startDate: Dayjs, endDate: Dayjs) => {
    return maxDate.isAfter(startDate) && maxDate.isBefore(endDate) ? maxDate : endDate;
  };

  const thisYearToDate: [Dayjs, Dayjs] = [
    currentDate.startOf("year"),
    adjustEndDate(currentDate.startOf("year"), currentDate.endOf("day")),
  ];
  const lastQuarter: [Dayjs, Dayjs] = [
    currentDate.subtract(3, "month").startOf("quarter"),
    adjustEndDate(
      currentDate.subtract(3, "month").startOf("quarter"),
      currentDate.subtract(1, "month").endOf("quarter")
    ),
  ];
  const thisQuarterToDate: [Dayjs, Dayjs] = [
    currentQuarterStart,
    adjustEndDate(currentQuarterStart, currentDate.endOf("day")),
  ];
  const lastYear: [Dayjs, Dayjs] = [
    currentDate.subtract(1, "year").startOf("year"),
    adjustEndDate(
      currentDate.subtract(1, "year").startOf("year"),
      currentDate.subtract(1, "year").endOf("year")
    ),
  ];
  const lastFinancialYear: [Dayjs, Dayjs] = [
    startOfFinancialYear.subtract(1, "year"),
    adjustEndDate(
      startOfFinancialYear.subtract(1, "year"),
      startOfFinancialYear.subtract(1, "day")
    ),
  ];
  const thisFinancialYearToDate: [Dayjs, Dayjs] = [
    startOfFinancialYear,
    adjustEndDate(startOfFinancialYear, currentDate.endOf("day")),
  ];

  // Assuming maxDate, currentDate, currentQuarterStart, and startOfFinancialYear are defined above this snippet

  const ranges = [
    { label: "All Time", value: allTime },
    { label: "Last 3 months", value: last3Months },
    { label: "Last 6 months", value: last6Months },
    { label: "Last 9 months", value: last9Months },
    { label: "Last 12 months", value: last12Months },
    { label: "This year to date", value: thisYearToDate },
    { label: "Last quarter", value: lastQuarter },
    { label: "This quarter to date", value: thisQuarterToDate },
    { label: "Last year", value: lastYear },
    { label: "Last financial year", value: lastFinancialYear },
    { label: "This financial year to date", value: thisFinancialYearToDate },
  ];

  const disabledStyle = {
    width: "100%",
    background: "none",
    color: "gray",
    border: "none",
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    height: "22px",
    fontSize: "14px",
    font: "inherit",
  };

  const dateRangeOptions = ranges.map(({ label, value }) => {
    const [optionStartDate, optionEndDate] = value;
    // The `null` argument specifies that no granularity (like 'day' or 'month') is considered,
    // and the `[]` argument includes the start and end dates in the range
    const isStartWithinRange = optionStartDate.isBetween(
      minDate.startOf("day"),
      maxDate.endOf("day"),
      null,
      "[]"
    );
    const isEndWithinRange = optionEndDate.isBetween(
      minDate.startOf("day"),
      maxDate.endOf("day"),
      null,
      "[]"
    );

    if (isStartWithinRange || isEndWithinRange) {
      return { label: <span style={{ padding: "0 8px" }}>{label}</span>, value };
    }
    return {
      label: (
        <button disabled style={disabledStyle}>
          {label}
        </button>
      ),
      value,
    };
  });

  return dateRangeOptions;
};
